import type { Program } from '@/types';

type ProgramLike = Pick<
  Program,
  | 'title'
  | 'description'
  | 'tags'
  | 'discovery_rule_type'
  | 'discovery_window_start'
  | 'discovery_window_end'
  | 'discovery_zodiac_sign'
>;

export type DiscoveryRuleType =
  | 'always'
  | 'seasonal_window'
  | 'birthday_window'
  | 'zodiac_sign';

export type ZodiacSign =
  | 'aries'
  | 'taurus'
  | 'gemini'
  | 'cancer'
  | 'leo'
  | 'virgo'
  | 'libra'
  | 'scorpio'
  | 'sagittarius'
  | 'capricorn'
  | 'aquarius'
  | 'pisces';

export interface DiscoveryProfileSignals {
  birthday?: string | null;
}

export const ZODIAC_SIGN_OPTIONS: ZodiacSign[] = [
  'aries',
  'taurus',
  'gemini',
  'cancer',
  'leo',
  'virgo',
  'libra',
  'scorpio',
  'sagittarius',
  'capricorn',
  'aquarius',
  'pisces',
];

export const DISCOVERY_RULE_OPTIONS: Array<{ value: DiscoveryRuleType; label: string }> = [
  { value: 'always', label: 'Always eligible' },
  { value: 'seasonal_window', label: 'Only during a seasonal window' },
  { value: 'birthday_window', label: 'Only near the user birthday' },
  { value: 'zodiac_sign', label: 'Only for a matching zodiac sign' },
];

interface MonthDay {
  month: number;
  day: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MONTH_DAY_PATTERN = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

const ZODIAC_RANGES: Array<{ sign: ZodiacSign; start: MonthDay; end: MonthDay }> = [
  { sign: 'capricorn', start: { month: 12, day: 22 }, end: { month: 1, day: 19 } },
  { sign: 'aquarius', start: { month: 1, day: 20 }, end: { month: 2, day: 18 } },
  { sign: 'pisces', start: { month: 2, day: 19 }, end: { month: 3, day: 20 } },
  { sign: 'aries', start: { month: 3, day: 21 }, end: { month: 4, day: 19 } },
  { sign: 'taurus', start: { month: 4, day: 20 }, end: { month: 5, day: 20 } },
  { sign: 'gemini', start: { month: 5, day: 21 }, end: { month: 6, day: 20 } },
  { sign: 'cancer', start: { month: 6, day: 21 }, end: { month: 7, day: 22 } },
  { sign: 'leo', start: { month: 7, day: 23 }, end: { month: 8, day: 22 } },
  { sign: 'virgo', start: { month: 8, day: 23 }, end: { month: 9, day: 22 } },
  { sign: 'libra', start: { month: 9, day: 23 }, end: { month: 10, day: 22 } },
  { sign: 'scorpio', start: { month: 10, day: 23 }, end: { month: 11, day: 21 } },
  { sign: 'sagittarius', start: { month: 11, day: 22 }, end: { month: 12, day: 21 } },
];

function parseMonthDay(dateValue?: string | null): MonthDay | null {
  if (!dateValue) return null;

  const trimmed = dateValue.trim();
  const parts = trimmed.split('-').map((part) => Number(part));
  if (parts.length < 2 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }

  const month = parts.length === 3 ? parts[1] : parts[0];
  const day = parts.length === 3 ? parts[2] : parts[1];

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  return { month, day };
}

function formatMonthDayParts(value: MonthDay): string {
  return `${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`;
}

export function isValidMonthDay(value?: string | null): boolean {
  if (!value) return false;
  return MONTH_DAY_PATTERN.test(value.trim());
}

export function normalizeMonthDayInput(value?: string | null): string {
  if (!value) return '';

  const trimmed = value.trim();
  const slashNormalized = trimmed.replace(/\//g, '-');
  const monthDay = parseMonthDay(slashNormalized);
  if (!monthDay) return trimmed;

  return formatMonthDayParts(monthDay);
}

function monthDayToNumber(value: MonthDay): number {
  return value.month * 100 + value.day;
}

function isWithinAnnualRange(date: Date, start: MonthDay, end: MonthDay): boolean {
  const current = monthDayToNumber({
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
  const startValue = monthDayToNumber(start);
  const endValue = monthDayToNumber(end);

  if (startValue <= endValue) {
    return current >= startValue && current <= endValue;
  }

  return current >= startValue || current <= endValue;
}

function getBirthdayDistanceInDays(now: Date, birthday: MonthDay | null): number | null {
  if (!birthday) return null;

  const currentUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const candidateYears = [now.getUTCFullYear() - 1, now.getUTCFullYear(), now.getUTCFullYear() + 1];

  const distances = candidateYears.map((year) => {
    const birthdayUtc = Date.UTC(year, birthday.month - 1, birthday.day);
    return Math.round(Math.abs(birthdayUtc - currentUtc) / MS_PER_DAY);
  });

  return Math.min(...distances);
}

export function getZodiacSignForBirthday(birthday?: string | null): ZodiacSign | null {
  const monthDay = parseMonthDay(birthday);
  if (!monthDay) return null;

  const current = monthDayToNumber(monthDay);
  const match = ZODIAC_RANGES.find(({ start, end }) => {
    const startValue = monthDayToNumber(start);
    const endValue = monthDayToNumber(end);

    if (startValue <= endValue) {
      return current >= startValue && current <= endValue;
    }

    return current >= startValue || current <= endValue;
  });

  return match?.sign ?? null;
}

export function isProgramEligibleForDiscovery(
  program: ProgramLike,
  profileSignals?: DiscoveryProfileSignals,
  now = new Date()
): boolean {
  const ruleType = (program.discovery_rule_type ?? 'always') as DiscoveryRuleType;
  const birthday = parseMonthDay(profileSignals?.birthday ?? null);
  const userSign = getZodiacSignForBirthday(profileSignals?.birthday ?? null);

  if (ruleType === 'always') return true;

  if (ruleType === 'seasonal_window') {
    const start = parseMonthDay(program.discovery_window_start ?? null);
    const end = parseMonthDay(program.discovery_window_end ?? null);
    if (!start || !end) return false;
    return isWithinAnnualRange(now, start, end);
  }

  if (ruleType === 'birthday_window') {
    const daysFromBirthday = getBirthdayDistanceInDays(now, birthday);
    return daysFromBirthday != null && daysFromBirthday <= 14;
  }

  if (ruleType === 'zodiac_sign') {
    if (!userSign) return false;
    return program.discovery_zodiac_sign === userSign;
  }

  return false;
}

export function filterProgramsForDiscovery<T extends ProgramLike>(
  programs: T[],
  profileSignals?: DiscoveryProfileSignals,
  now = new Date()
): T[] {
  return programs.filter((program) => isProgramEligibleForDiscovery(program, profileSignals, now));
}
